import {
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { addDays, format, parse } from 'date-fns';
import { FC } from 'react';
import { DriverEntity } from 'src/entities/Driver.entity';
import { ShiftPatternEntity } from 'src/entities/ShiftPattern.entity';
import { CellEditorFormValues } from 'src/pages/TruckShifts/cellEditor';
import { TimeSelectField } from 'src/pages/TruckShifts/presenters/TimeSelectField.presenter';

type Props = {
  value: CellEditorFormValues;
  onChange: (v: CellEditorFormValues) => void;
  editFieldsLocked: boolean;
  allPatterns: ShiftPatternEntity[];
  driverOptions: DriverEntity[];
  onDelete: () => void;
  deletable: boolean;
  cellDate: string;
  truncatable: boolean;
  onTruncate: () => void;
};

export const CellShiftCard: FC<Props> = ({
  value, onChange, editFieldsLocked,
  allPatterns, driverOptions, onDelete, deletable,
  cellDate, truncatable, onTruncate,
}) => {
  const patch = (next: Partial<CellEditorFormValues>) => onChange({ ...value, ...next });

  const crossDay = value.startDate !== value.endDate;
  const makeCrossDay = () => {
    const next = format(addDays(parse(value.startDate, 'yyyy-MM-dd', new Date()), 1), 'yyyy-MM-dd');
    patch({ endDate: next, mode: 'custom' });
  };

  const selectedPattern = (value.patternId != null ? allPatterns.find((p) => p.id === value.patternId) : undefined) ?? null;
  const dotColor = selectedPattern?.color ?? '#eceff1';
  const cardLabel = selectedPattern?.name ?? 'カスタム';

  const selectedDriver = driverOptions.find((d) => d.id === value.driverId) ?? null;

  return (
    <Stack spacing={1} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      {/* ヘッダ行 */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
        <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>{cardLabel}</Typography>
        <Button
          size="small"
          color="error"
          variant="text"
          disabled={!deletable}
          onClick={onDelete}
          aria-label="この勤務を削除"
          sx={{ minWidth: 0, fontSize: '0.75rem' }}
        >
          🗑️ この勤務を削除
        </Button>
      </Stack>
      {truncatable && (
        <Button size="small" color="warning" disabled={editFieldsLocked} onClick={onTruncate} sx={{ textTransform: 'none' }}>
          {cellDate} で打ち切り（以降は未割当）
        </Button>
      )}

      {editFieldsLocked && (
        <Typography variant="caption" color="warning.main">固定中の勤務です（ドライバー割当のみ変更できます）</Typography>
      )}

      {/* 開始 */}
      <Stack direction="row" spacing={1} alignItems="center">
        {crossDay && (
          <TextField
            type="date"
            size="small"
            label="開始日"
            value={value.startDate}
            disabled={editFieldsLocked}
            onChange={(e) => patch({ startDate: e.target.value, mode: 'custom' })}
            sx={{ flex: 1 }}
            InputLabelProps={{ shrink: true }}
          />
        )}
        <TimeSelectField
          label="開始"
          value={value.startTime}
          disabled={editFieldsLocked}
          onChange={(v) => patch({ startTime: v, mode: 'custom' })}
          sx={{ width: 110 }}
        />
      </Stack>
      {/* 終了 */}
      <Stack direction="row" spacing={1} alignItems="center">
        {crossDay && (
          <TextField
            type="date"
            size="small"
            label="終了日"
            value={value.endDate}
            disabled={editFieldsLocked}
            onChange={(e) => patch({ endDate: e.target.value, mode: 'custom' })}
            sx={{ flex: 1 }}
            InputLabelProps={{ shrink: true }}
          />
        )}
        <TimeSelectField
          label="終了"
          value={value.endTime}
          disabled={editFieldsLocked}
          onChange={(v) => patch({ endTime: v, mode: 'custom' })}
          sx={{ width: 110 }}
        />
      </Stack>
      {!crossDay && !editFieldsLocked && (
        <Button size="small" variant="text" onClick={makeCrossDay} sx={{ alignSelf: 'flex-start', fontSize: '0.75rem' }}>
          + 日跨ぎにする
        </Button>
      )}
      <TextField
        type="number"
        size="small"
        label="労働時間(h)"
        value={value.workingHours}
        disabled={editFieldsLocked}
        inputProps={{ min: 0, step: 0.5 }}
        onChange={(e) => patch({ workingHours: e.target.value === '' ? '' : Number(e.target.value), mode: 'custom' })}
      />

      {/* ドライバー Autocomplete（常に表示） */}
      <Autocomplete
        freeSolo
        size="small"
        options={driverOptions}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        getOptionKey={(option) => (typeof option === 'string' ? option : option.id)}
        value={selectedDriver}
        inputValue={value.driverName}
        onChange={(_, option) => {
          if (option === null) patch({ driverId: null, driverName: '' });
          else if (typeof option === 'string') patch({ driverId: null, driverName: option });
          else patch({ driverId: option.id, driverName: option.name });
        }}
        onInputChange={(_, input, reason) => {
          if (reason === 'input' || reason === 'clear') patch({ driverId: null, driverName: input });
        }}
        renderInput={(params) => <TextField {...params} label="ドライバー" />}
      />
    </Stack>
  );
};
